import { Blend, Grid } from 'lucide-react';
import { useRecoilValue } from 'recoil';
import { ImageViewMode } from '~/engine/core/render/renderers/ImageRenderer';
import usePetImage from '~/hooks/image/usePetImage';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import {
    checkerboardDensityState,
    imageSecondaryAmountState,
    selectedSecondaryImageModeState,
} from '~/state/atoms/image/imageAtoms';
import { OverlayLabel, OverlayRow } from '../../OverlayLabel';
import { OverlayMenu, OverlayMenuOption } from '../../OverlayMenu';
import FusionColorSelector from './FusionColorSelector';
import PetFusionColorSelector from './PetFusionColorSelector';

const blendingModeOptions: OverlayMenuOption<ImageViewMode>[] = [
    {
        value: ImageViewMode.AlphaBlending,
        label: 'Alpha Blending',
        icon: <Blend size={9} />,
    },
    {
        value: ImageViewMode.Checkerboard,
        label: 'Checkerboard',
        icon: <Grid size={9} />,
    },
];

const getShortLabel = (mode: ImageViewMode): string => {
    switch (mode) {
        case ImageViewMode.AlphaBlending:
            return 'Alpha';
        case ImageViewMode.Checkerboard:
            return 'Checker';
        default:
            return 'Mode';
    }
};

const BlendingModeSelector = () => {
    const selectedViewMode = useRecoilValue(selectedSecondaryImageModeState);
    const imageSecondaryAmount = useRecoilValue(imageSecondaryAmountState);
    const checkerboardDensity = useRecoilValue(checkerboardDensityState);
    const { updateImageViewMode } = useViewportEnvironment();
    const { isPetSecondary } = usePetImage();

    const getCenterValue = () => {
        if (selectedViewMode === ImageViewMode.AlphaBlending) {
            return `${imageSecondaryAmount}%`;
        }
        if (selectedViewMode === ImageViewMode.Checkerboard) {
            return `${Math.round(checkerboardDensity * 100)}%`;
        }
        return '';
    };

    const ModeIcon =
        selectedViewMode === ImageViewMode.AlphaBlending ? Blend : Grid;

    return (
        <OverlayRow sx={{ alignItems: 'center', px: 0.25, ml: -0.25 }}>
            <ModeIcon size={11} />
            <OverlayMenu
                options={blendingModeOptions}
                value={selectedViewMode}
                onChange={updateImageViewMode}
                renderLabel={() => getShortLabel(selectedViewMode)}
            />
            <OverlayLabel color="primary.main">{getCenterValue()}</OverlayLabel>

            {isPetSecondary ? (
                <PetFusionColorSelector />
            ) : (
                <FusionColorSelector />
            )}
        </OverlayRow>
    );
};

export default BlendingModeSelector;
